import { clearCurrentUser, setCurrentUser, syncUserAccount } from '_store/auth';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';

const checkUserSession = async (dispatch: any) => {
  try {
    const { username, userId, signInDetails } = await getCurrentUser();

    const attributes = await fetchUserAttributes();

    if (userId) {
      dispatch(
        setCurrentUser({
          user_id: userId,
          username,
          attributes,
          signInDetails,
        }),
      );
      // Sync user account details after setting current user
      dispatch(syncUserAccount());
    }
  } catch (error) {
    dispatch(clearCurrentUser());
    console.error('Error checking user session:', error);
  }
};

// Generate a random 6 digit number
const generateRandom6DigitNumber = () => {
  return Math.floor(100000 + Math.random() * 900000);
};

// Waiver and booking timestamps are stored as UTC. SQL datetime2 values are
// serialized without a Z, and `new Date("2026-10-26T18:27:07")` would treat
// that instant as local time (11:27 AM Pacific showing as 6:27 PM).
const parseApiDateTime = (dateString: string): Date => {
  const value = dateString.trim();
  const naiveIso =
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/.exec(value);
  if (naiveIso) {
    return new Date(`${naiveIso[1]}T${naiveIso[2]}Z`);
  }
  return new Date(value);
};

const formatDateTimeUtc = (dateString: string) => {
  if (!dateString) return '';
  const date = parseApiDateTime(dateString);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { timeZone: 'UTC' });
};

const formatDateTimeLocal = (dateString: string) => {
  if (!dateString) return '';
  const date = parseApiDateTime(dateString);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  // Convert UTC to local time - toLocaleString uses the user's local timezone by default
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return '';
  const date = parseApiDateTime(dateString);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

// Format date helper
const formatDate = (dateString?: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  // API DateTime.MinValue / year-0001 placeholders are treated as missing
  if (date.getFullYear() <= 1) return '';
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
};

const formatWaiverSignedBy = (signedBy?: string | null) => {
  const name = (signedBy || '').trim();
  if (!name || name === 'Not Listed') return '';
  return name;
};

// True when a date-only API value ("YYYY-MM-DD" or "M/D/YYYY ...") is today's local calendar date.
// Parsed by hand: new Date("YYYY-MM-DD") is UTC and can land on the previous day in Pacific time.
const isDateToday = (dateString?: string | null, now = new Date()): boolean => {
  if (!dateString) return false;
  const value = dateString.trim();
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const us = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  let date: Date;
  if (iso) date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  else if (us) date = new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  else date = new Date(value);
  if (isNaN(date.getTime())) return false;
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const formatTime = (timeString: string) => {
  return timeString.split('.')[0]; // Remove milliseconds
};

// Format time in 24-hour format (HH:MM)
const formatTime24Hour = (timeString: string): string => {
  if (!timeString) return '';

  // Handle different time formats
  if (timeString.includes(':')) {
    // Split the time string into parts
    const timeParts = timeString.split(':');

    if (timeParts.length >= 2) {
      const hours = timeParts[0].padStart(2, '0');
      const minutes = timeParts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  }

  // If it's a full datetime string, try to extract time part
  try {
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch (error) {
    // Fallback: return original string
  }

  return timeString;
};

// Format time in 12-hour format (h:MMam/pm)
const formatTime12Hour = (timeString: string): string => {
  if (!timeString) return '';

  console.log('timeString', timeString);

  // Remove milliseconds if present
  const cleanedTime = timeString.split('.')[0];

  // Handle different time formats
  if (cleanedTime.includes(':')) {
    // Split the time string into parts
    const timeParts = cleanedTime.split(':');

    if (timeParts.length >= 2) {
      let hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1].padStart(2, '0');

      // Determine AM or PM
      const period = hours >= 12 ? 'PM' : 'AM';

      // Convert to 12-hour format
      if (hours === 0) {
        hours = 12; // Midnight
      } else if (hours > 12) {
        hours = hours - 12;
      }

      return `${hours}:${minutes}${period}`;
    }
  }

  // If it's a full datetime string, try to extract time part
  try {
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date
        .toLocaleTimeString('en-US', {
          hour12: true,
          hour: 'numeric',
          minute: '2-digit',
        })
        .replace(/\s/g, ''); // Remove space between time and AM/PM
    }
  } catch (error) {
    // Fallback: return original string
  }

  return timeString;
};

const MAX_AGE_YEARS = 120;

const toLocalDateOnlyString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get minimum allowed date for date of birth (120 years ago, local calendar)
const getMinDateOfBirth = (): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - MAX_AGE_YEARS);
  return toLocalDateOnlyString(date);
};

// Get maximum allowed date for date of birth (today, local calendar)
const getMaxDateOfBirth = (): string => {
  return toLocalDateOnlyString(new Date());
};

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getDaysInMonth = (month: number, year: number): number => {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return daysInMonth[month - 1] || 0;
};

// Validate and sanitize date of birth input
const validateDateOfBirth = (dateString: string): string => {
  if (!dateString) return dateString;

  // Check if date is in YYYY-MM-DD format
  const datePattern = /^(\d{1,6})-(\d{2})-(\d{2})$/;
  const match = dateString.match(datePattern);

  if (match) {
    const year = match[1];
    const month = match[2];
    const day = match[3];

    // Only allow exactly 4 digits for year
    if (year.length !== 4) {
      return '';
    }

    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    if (monthNum < 1 || monthNum > 12) {
      return '';
    }

    const maxDays = getDaysInMonth(monthNum, yearNum);
    if (dayNum < 1 || dayNum > maxDays) {
      return '';
    }

    const normalized = `${year}-${month}-${day}`;
    const minDate = getMinDateOfBirth();
    const maxDate = getMaxDateOfBirth();

    if (normalized < minDate || normalized > maxDate) {
      return '';
    }

    return normalized;
  }

  return '';
};

const isPlausibleDateOfBirth = (dateOfBirth: string): boolean => {
  if (!dateOfBirth) return false;
  return validateDateOfBirth(dateOfBirth) === dateOfBirth;
};

const getDateOfBirthError = (dateOfBirth: string): string | null => {
  if (!dateOfBirth) return null;
  if (isPlausibleDateOfBirth(dateOfBirth)) return null;
  return 'Enter a valid date of birth';
};

// Calculate age from date of birth
const calculateAge = (dateOfBirth: string): number => {
  if (!dateOfBirth) return 0;

  // Validate the date format first
  const validatedDate = validateDateOfBirth(dateOfBirth);
  if (!validatedDate) return 0;

  const today = new Date();
  const [year, month, day] = validatedDate.split('-').map(Number);
  const birthDate = new Date(year, month - 1, day);

  // Check if birthDate is valid
  if (isNaN(birthDate.getTime())) return 0;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  // Return 0 for invalid ages (negative or unreasonably high)
  if (age < 0 || age > MAX_AGE_YEARS) return 0;

  return age;
};

// Determine if person is adult (13+) or child (3-12)
const getAgeGroup = (dateOfBirth: string): string => {
  const age = calculateAge(dateOfBirth);
  if (age >= 13) return 'adult';
  if (age >= 3) return 'child';
  return ''; // Invalid age (too young)
};

// Phone number validation function
const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone) return false; // Phone is required
  const cleaned = phone.replace(/\D/g, ''); // Remove all non-digits
  return cleaned.length === 10;
};

// Email validation function
const isValidEmail = (email: string): boolean => {
  if (!email) return false; // Email is required
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Decodes an encoded link-only URL code
 * This is a simple obfuscation - not cryptographically secure
 * The backend should encode the linkOnlyUrlCode before sending it
 * @param encodedCode - The encoded string from the API
 * @returns The decoded link code or null if decoding fails
 */
const decodeLinkOnlyUrlCode = (
  encodedCode: string | null | undefined,
): string | null => {
  if (!encodedCode) return null;

  try {
    // Simple Base64 decode (backend should encode with Base64 + optional prefix/suffix)
    // If backend uses additional encoding, adjust this function accordingly
    const decoded = atob(encodedCode);
    return decoded;
  } catch (error) {
    console.error('Error decoding linkOnlyUrlCode:', error);
    return null;
  }
};

/**
 * Parses a date from a link code (e.g., "oct31BATS" -> "2025-10-31")
 * Format: month abbreviation (3 letters) + day (1-2 digits) + optional suffix
 * @param linkCode - The link code string (e.g., "oct31BATS")
 * @returns ISO date string (YYYY-MM-DD) or null if parsing fails
 */
const parseDateFromLinkCode = (
  linkCode: string | null | undefined,
): string | null => {
  if (!linkCode) return null;

  // Month abbreviations mapping
  const monthMap: { [key: string]: number } = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  try {
    // Match pattern: 3-letter month + 1-2 digit day + optional suffix
    const match = linkCode
      .toLowerCase()
      .match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(\d{1,2})/);

    if (!match) return null;

    const monthName = match[1];
    const day = parseInt(match[2], 10);
    const month = monthMap[monthName];

    if (isNaN(month) || isNaN(day) || day < 1 || day > 31) return null;

    // Get current year and check if the date has passed
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // Create the date for current year (at midnight)
    const parsedDate = new Date(currentYear, month, day, 0, 0, 0, 0);

    // Compare dates without time - get today's date at midnight for comparison
    const todayAtMidnight = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      0,
      0,
      0,
      0,
    );

    // Only use next year if the parsed date is before today (not equal to today)
    // If today is Oct 31 and we parse "oct31", we want Oct 31 of current year, not next year
    if (parsedDate.getTime() < todayAtMidnight.getTime()) {
      parsedDate.setFullYear(currentYear + 1);
    }

    // Validate the date (e.g., Feb 30 would be invalid)
    if (parsedDate.getMonth() !== month || parsedDate.getDate() !== day) {
      return null;
    }

    // Return ISO string format (YYYY-MM-DD)
    const year = parsedDate.getFullYear();
    const monthStr = String(month + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');

    return `${year}-${monthStr}-${dayStr}`;
  } catch (error) {
    console.error('Error parsing date from link code:', error);
    return null;
  }
};

export const SEASON_PASS_MAX_SESSIONS = 60;

const isSeasonPass = (pass: {
  passType?: string | null;
  passName?: string | null;
}): boolean => {
  const type = (pass.passType || '').toLowerCase().trim();
  const name = (pass.passName || '').toLowerCase().trim();
  return (
    type === 'season pass' || type === 'season_pass' || name === 'season pass'
  );
};

const getEffectiveMaxUses = (pass: {
  passType?: string | null;
  passName?: string | null;
  maxUses?: number | null;
}): number | null => {
  if (pass.maxUses != null && Number(pass.maxUses) > 0) {
    return Number(pass.maxUses);
  }
  if (isSeasonPass(pass)) {
    return SEASON_PASS_MAX_SESSIONS;
  }
  return null;
};

const canRedeemSkatingPass = (pass: {
  passType?: string | null;
  passName?: string | null;
  redeemedCount?: number | null;
  maxUses?: number | null;
  isActive?: boolean;
}): boolean => {
  if (pass.isActive === false) {
    return false;
  }

  const effectiveMaxUses = getEffectiveMaxUses(pass);
  if (effectiveMaxUses != null) {
    return Number(pass.redeemedCount || 0) < effectiveMaxUses;
  }

  return true;
};

const helperMethods = {
  checkUserSession,
  generateRandom6DigitNumber,
  formatDateTimeUtc,
  formatDateTimeLocal,
  formatDateTime,
  formatDate,
  formatWaiverSignedBy,
  isDateToday,
  formatTime,
  formatTime24Hour,
  formatTime12Hour,
  getMinDateOfBirth,
  getMaxDateOfBirth,
  validateDateOfBirth,
  isPlausibleDateOfBirth,
  getDateOfBirthError,
  calculateAge,
  getAgeGroup,
  isValidPhoneNumber,
  isValidEmail,
  decodeLinkOnlyUrlCode,
  parseDateFromLinkCode,
  isSeasonPass,
  getEffectiveMaxUses,
  canRedeemSkatingPass,
  SEASON_PASS_MAX_SESSIONS,
};

export default helperMethods;
